/// <reference types="google.maps" />

/**
 * Consumer · Heat Map — where the freight is, by H3 area.
 *
 * The triple-slash reference is scoped here on purpose: gluon's tsconfig sets
 * `"types": ["node"]`, so no other global type package is included project-wide.
 * Adding `google.maps` there would change what every file in the app can see
 * for the sake of one component.
 *
 * FROM THE PUBLIC API, NOT FROM THE HOURLY TABLE. This page used to aggregate
 * `loadpipe_intel_hourly` per request — 5.4 s of CPU, measured, because every
 * viewer re-derived the same answer. It now reads `GET /market-intel/areas/heat/v1`,
 * which the market-intel module answers from its resident snapshot, the same
 * way every other client does (ADR-033 §4.6). Equipment is required: the map is
 * per truck type.
 *
 * WHY THE CLIENT DRAWS THE POLYGONS. A cell's boundary is a pure function of
 * its id, so shipping coordinate pairs per cell would be sending data the
 * receiver can compute for free. `cellToBoundary` runs here instead.
 */

import Box from '@mui/joy/Box'
import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import CircularProgress from '@mui/joy/CircularProgress'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Typography from '@mui/joy/Typography'
import { Wrapper } from '@googlemaps/react-wrapper'
import { MARKET_INTEL_TIERS, formatMarketIntelTierArea, type MarketIntelAreaHeat, type MarketIntelDirection, type MarketIntelTier } from '@numeo/types-core'
import { cellToBoundary } from 'h3-js'
import * as React from 'react'
import { marketIntelApi } from 'api/marketIntel'
import { AreaPanel } from './components/AreaPanel'
import { EquipmentSelect, SnapshotAge, money } from './components/marketIntel'
import { ErrorState, Loading, formatNumber, useLoadPipelineData } from './components/shared'

/** Continental US, which is the whole extent the pipeline covers. */
const US_CENTER = { lat: 39.5, lng: -98.35 }

/**
 * Colour by volume on a log scale.
 *
 * Freight volume is heavily skewed — a handful of areas carry orders of
 * magnitude more than the median — so a linear ramp paints one area red and
 * everything else the same pale colour, which is a picture of the outlier
 * rather than of the market.
 */
function colorFor(observations: number, max: number): string {
    if (max <= 0) return '#cfd8dc'
    const t = Math.log10(1 + observations) / Math.log10(1 + max)
    const hue = 220 - 220 * t // blue (cold) → red (hot)
    return `hsl(${hue}, 78%, 48%)`
}

function AreaMap({ areas, onPick }: { areas: MarketIntelAreaHeat[]; onPick: (a: MarketIntelAreaHeat) => void }) {
    const ref = React.useRef<HTMLDivElement>(null)
    const mapRef = React.useRef<google.maps.Map | null>(null)
    const shapesRef = React.useRef<google.maps.Polygon[]>([])

    React.useEffect(() => {
        if (ref.current && !mapRef.current) {
            mapRef.current = new google.maps.Map(ref.current, {
                center: US_CENTER,
                zoom: 4,
                disableDefaultUI: true,
                zoomControl: true,
                // A data overlay is unreadable on a photographic basemap.
                mapTypeId: 'roadmap',
            })
        }
    }, [])

    React.useEffect(() => {
        const map = mapRef.current
        if (!map) return

        // Polygons are imperative objects, not React children — an old set left
        // attached would stack under the new one and darken every area.
        for (const shape of shapesRef.current) shape.setMap(null)
        shapesRef.current = []

        const max = areas.reduce((m, a) => Math.max(m, a.observations), 0)
        for (const area of areas) {
            const path = cellToBoundary(area.cell).map(([lat, lng]) => ({ lat, lng }))
            const polygon = new google.maps.Polygon({
                paths: path,
                strokeColor: '#263238',
                strokeOpacity: 0.25,
                strokeWeight: 1,
                fillColor: colorFor(area.observations, max),
                fillOpacity: 0.55,
                map,
            })
            polygon.addListener('click', () => onPick(area))
            shapesRef.current.push(polygon)
        }

        return () => {
            for (const shape of shapesRef.current) shape.setMap(null)
            shapesRef.current = []
        }
    }, [areas, onPick])

    return <Box ref={ref} sx={{ height: 560, borderRadius: 'sm' }} />
}

export default function LoadPipelineHeatmapPage() {
    const [tier, setTier] = React.useState<MarketIntelTier>('r4')
    const [direction, setDirection] = React.useState<MarketIntelDirection>('out')
    const [equipment, setEquipment] = React.useState<string>('V')
    const [picked, setPicked] = React.useState<MarketIntelAreaHeat | null>(null)

    const { data, error, loading } = useLoadPipelineData(() => marketIntelApi.getHeat(equipment, tier, direction), [tier, direction, equipment])

    // A pick from the previous map would point at a cell that is not drawn.
    React.useEffect(() => setPicked(null), [tier, direction, equipment])

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
    const areas = React.useMemo(() => [...(data?.areas ?? [])].sort((a, b) => b.observations - a.observations), [data])

    return (
        <Box>
            <Typography level="body-sm" sx={{ mb: 2, color: 'text.tertiary' }}>
                Loads per H3 area over the snapshot window, from the Market Intel API. Click an area for where freight from it pays best and what is open right
                now.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <EquipmentSelect value={equipment} onChange={setEquipment} />
                <Select size="sm" value={tier} onChange={(_, v) => v && setTier(v)} sx={{ minWidth: 200 }}>
                    {MARKET_INTEL_TIERS.map((t) => (
                        <Option key={t} value={t}>
                            {t} · {formatMarketIntelTierArea(t)}
                        </Option>
                    ))}
                </Select>
                <Select size="sm" value={direction} onChange={(_, v) => v && setDirection(v)} sx={{ minWidth: 180 }}>
                    <Option value="out">Outbound (origins)</Option>
                    <Option value="in">Inbound (destinations)</Option>
                </Select>
                <Chip size="sm" variant="soft">
                    {formatNumber(areas.length)} areas
                </Chip>
                {data ? <SnapshotAge provenance={data} /> : null}
            </Box>

            {loading ? <Loading /> : null}
            {error ? <ErrorState error={error} /> : null}

            {!loading && !error ? (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <Card sx={{ flex: '1 1 640px', minWidth: 360, p: 1 }}>
                        {apiKey ? (
                            <Wrapper
                                apiKey={apiKey}
                                render={(status) =>
                                    status === 'FAILURE' ? (
                                        <Typography level="body-sm" sx={{ p: 2 }}>
                                            Unable to load Google Maps — check the API key configuration.
                                        </Typography>
                                    ) : (
                                        <Box sx={{ height: 560, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <CircularProgress />
                                        </Box>
                                    )
                                }
                            >
                                <AreaMap areas={areas} onPick={setPicked} />
                            </Wrapper>
                        ) : (
                            <Typography level="body-sm" sx={{ p: 2 }}>
                                REACT_APP_GOOGLE_MAPS_API_KEY is not set, so the map cannot render. The list beside it still works.
                            </Typography>
                        )}
                    </Card>

                    <Card sx={{ flex: '0 1 380px', minWidth: 300 }}>
                        <Typography level="title-md">{picked ? 'Selected area' : 'Busiest areas'}</Typography>
                        {picked ? (
                            <AreaPanel area={picked} tier={tier} equipment={equipment} onClear={() => setPicked(null)} />
                        ) : (
                            <Box sx={{ display: 'grid', gap: 0.5 }}>
                                {areas.slice(0, 12).map((a) => (
                                    <Box key={a.cell} sx={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setPicked(a)}>
                                        <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                            {a.cell.slice(0, 9)}…
                                        </Typography>
                                        <Typography level="body-xs">
                                            {formatNumber(a.observations)} · {a.p50 == null ? '—' : `${money(a.p50)}/mi`}
                                        </Typography>
                                    </Box>
                                ))}
                                {areas.length === 0 ? <Typography level="body-sm">No market data for {equipment} at this tier.</Typography> : null}
                            </Box>
                        )}
                    </Card>
                </Box>
            ) : null}
        </Box>
    )
}
